import React, { useState, useEffect, useCallback } from 'react';
import { dealersApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search, Plus, Loader2, X, Users, Store, Phone, MapPin,
  Activity, ChevronLeft, ChevronRight, Filter, Package, Calendar,
  CreditCard, Pencil, Building2, FileText, Clock, Trash2,
} from 'lucide-react';
import { DateRangeFilter, getDateRange, DATE_PRESETS } from '@/components/ui/DateRangeFilter';

const DEALER_STATUS_COLORS = {
  'NEW': 'bg-blue-100 text-blue-700',
  'CONTACTED': 'bg-cyan-100 text-cyan-700',
  'FOLLOW UP': 'bg-yellow-100 text-yellow-700',
  'INTERESTED': 'bg-green-100 text-green-700',
  'NEGOTIATION': 'bg-amber-100 text-amber-700',
  'CALLBACK': 'bg-purple-100 text-purple-700',
  'RNR': 'bg-orange-100 text-orange-700',
  'RNR1': 'bg-orange-100 text-orange-700',
  'RNR2': 'bg-orange-200 text-orange-800',
  'RNR3': 'bg-red-100 text-red-700',
  'HOT LEAD': 'bg-red-100 text-red-700',
  'PAYMENT LINK SENT': 'bg-purple-100 text-purple-700',
  'ACTIVE': 'bg-emerald-100 text-emerald-700',
  'NOT INTERESTED': 'bg-gray-100 text-gray-600',
  'DEAD LEAD': 'bg-gray-200 text-gray-600',
  'WRONG NUMBER': 'bg-red-100 text-red-700',
};

// ========== Stats Cards ==========
function DealerStats({ cardCounts }) {
  const cards = [
    { label: 'Total Dealers', value: cardCounts.total || 0, color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: 'Contacted', value: cardCounts.contacted || 0, color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
    { label: 'Interested', value: cardCounts.interested || 0, color: 'bg-green-50 text-green-700 border-green-200' },
    { label: 'Active (Paid)', value: cardCounts.active || 0, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { label: 'RNR', value: cardCounts.rnr || 0, color: 'bg-orange-50 text-orange-700 border-orange-200' },
    { label: 'Active Packages', value: cardCounts.active_packages || 0, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dealer-stats">
      {cards.map((c) => (
        <div key={c.label} className={`rounded-xl border p-3 ${c.color}`}>
          <p className="text-[10px] font-medium uppercase tracking-wide opacity-70">{c.label}</p>
          <p className="text-2xl font-bold mt-0.5">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

// ========== Activity Timeline ==========
function DealerActivityLog({ dealerId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dealerId) return;
    setLoading(true);
    dealersApi.getActivities(dealerId)
      .then(res => setActivities(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealerId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-3" data-testid="dealer-activity-log">
      {activities.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No activities yet</p>}
      {activities.map((a) => (
        <div key={a.id} className="flex gap-3 text-sm border-l-2 border-blue-200 pl-3 py-1">
          <div className="flex-1">
            <p className="font-medium text-gray-800">{a.details || a.action}</p>
            <p className="text-xs text-gray-400">{a.user_name} &middot; {new Date(a.created_at).toLocaleString('en-IN')}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ========== Dealer Packages Tab ==========
function DealerPackagesTab({ dealerId, dealerName, dealerPhone, onRefresh, autoOpenPurchase = false, onCloseModal }) {
  const [packages, setPackages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPurchase, setShowPurchase] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  // Purchase form state — strings to allow empty field while typing
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [qtyStr, setQtyStr] = useState('1');
  const [validityStr, setValidityStr] = useState('45');
  const [discountStr, setDiscountStr] = useState('');
  const [linkExpiryHours, setLinkExpiryHours] = useState(48);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      dealersApi.getPackages(dealerId),
      dealersApi.getPackageTemplates(),
    ]).then(([pkgRes, tmplRes]) => {
      setPackages(pkgRes.data);
      setTemplates(tmplRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [dealerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-open purchase modal when triggered from table
  useEffect(() => {
    if (autoOpenPurchase && !loading && templates.length > 0) {
      openPurchaseModal();
    }
  }, [autoOpenPurchase, loading, templates.length]);

  const openPurchaseModal = () => {
    setSelectedTemplate(null);
    setQtyStr('1');
    setValidityStr('45');
    setDiscountStr('');
    setLinkExpiryHours(48);
    setShowPurchase(true);
  };

  // Derived numeric values for calculations
  const qty = parseInt(qtyStr, 10) || 0;
  const validityDays = parseInt(validityStr, 10) || 0;
  const discountAmount = parseInt(discountStr, 10) || 0;
  const pricePerInsp = selectedTemplate?.price_per_inspection || selectedTemplate?.price || 0;
  const subtotal = Math.round(pricePerInsp * qty);
  const finalAmount = Math.max(0, subtotal - discountAmount);
  const effectivePerInsp = qty > 0 ? Math.round(finalAmount / qty) : 0;

  const handleSelectTemplate = (templateId) => {
    const tmpl = templates.find(t => t.id === templateId);
    setSelectedTemplate(tmpl);
    if (tmpl) {
      setValidityStr(String(tmpl.default_validity_days || tmpl.validity_days || 45));
      setDiscountStr('');
    }
  };

  const handleSendPaymentLink = async () => {
    if (!selectedTemplate) { toast.error('Select a package'); return; }
    if (qty < 1) { toast.error('Enter number of inspections'); return; }
    if (finalAmount <= 0) { toast.error('Final amount must be greater than 0'); return; }
    setSendingLink(true);
    try {
      const res = await dealersApi.sendPaymentLink(dealerId, {
        package_template_id: selectedTemplate.id,
        no_of_inspections: qty,
        validity_days: validityDays,
        discount_amount: discountAmount,
        final_amount: finalAmount,
        link_expiry_hours: linkExpiryHours,
      });
      toast.success(`Payment link sent! Rs ${finalAmount.toLocaleString()}`);
      setShowPurchase(false);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send payment link');
    } finally { setSendingLink(false); }
  };

  const handleRecordManualPayment = async () => {
    if (!selectedTemplate) { toast.error('Select a package'); return; }
    setSaving(true);
    try {
      await dealersApi.purchasePackage(dealerId, {
        package_template_id: selectedTemplate.id,
        no_of_inspections: qty,
        validity_days: validityDays,
        discount_amount: discountAmount,
        final_amount: finalAmount,
        payment_method: 'manual',
      });
      toast.success('Package activated!');
      setShowPurchase(false);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleRazorpayCheckout = async () => {
    if (!selectedTemplate) { toast.error('Select a package'); return; }
    if (qty < 1 || finalAmount <= 0) { toast.error('Invalid amount'); return; }
    setSaving(true);
    try {
      const res = await dealersApi.createRazorpayOrder(dealerId, {
        package_template_id: selectedTemplate.id,
        package_name: selectedTemplate.name,
        no_of_inspections: qty,
        validity_days: validityDays,
        discount_amount: discountAmount,
        final_amount: finalAmount,
        price_per_inspection: pricePerInsp,
        subtotal,
      });
      const { order_id, amount, razorpay_key_id, dealer_name: dName, dealer_phone: dPhone, dealer_email: dEmail } = res.data;

      // Load Razorpay script
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      const rzp = new window.Razorpay({
        key: razorpay_key_id,
        amount,
        currency: 'INR',
        name: 'WiseDrive',
        description: `${selectedTemplate.name} x${qty} (${validityDays} days)`,
        order_id,
        prefill: { name: dName, contact: dPhone, email: dEmail },
        handler: async (response) => {
          try {
            await dealersApi.verifyPayment(dealerId, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Payment successful! Package activated.');
            setShowPurchase(false);
            fetchData();
            if (onRefresh) onRefresh();
            if (onCloseModal) onCloseModal();
          } catch { toast.error('Payment verification failed'); }
        },
        theme: { color: '#16a34a' },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const renderPurchaseDialogContent = () => (
    <DialogContent className="sm:max-w-lg p-0 max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-5 pb-4 border-b bg-gradient-to-r from-slate-50 to-white shrink-0">
        <DialogTitle className="text-base font-bold text-gray-900">
          New Package for {dealerName || 'Dealer'}
        </DialogTitle>
        <p className="text-xs text-gray-500 mt-0.5">Configure inspections, validity & pricing</p>
      </div>

      <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1 min-h-0">
        <div>
          <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Package Type</Label>
          <Select value={selectedTemplate?.id || ''} onValueChange={handleSelectTemplate}>
            <SelectTrigger className="mt-1.5 h-11 text-sm"><SelectValue placeholder="Choose a package" /></SelectTrigger>
            <SelectContent>
              {templates.filter(t => t.is_active).map(t => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name} — Rs {(t.price_per_inspection || t.price).toLocaleString()}/inspection
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedTemplate && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Inspections</Label>
                <input type="text" inputMode="numeric" value={qtyStr}
                  onChange={e => setQtyStr(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={() => { if (!qtyStr || parseInt(qtyStr) < 1) setQtyStr('1'); }}
                  className="mt-1.5 w-full h-12 border rounded-lg text-center text-xl font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  data-testid="pkg-qty-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Validity (Days)</Label>
                <input type="text" inputMode="numeric" value={validityStr}
                  onChange={e => setValidityStr(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={() => { if (!validityStr || parseInt(validityStr) < 1) setValidityStr('1'); }}
                  className="mt-1.5 w-full h-12 border rounded-lg text-center text-xl font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  data-testid="pkg-validity-input" />
                <p className="text-[10px] text-gray-400 mt-1 text-center">Default: {selectedTemplate.default_validity_days || selectedTemplate.validity_days || 45} days</p>
              </div>
            </div>

            <div className="rounded-xl border bg-slate-50 overflow-hidden">
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Rate per inspection</span>
                  <span>Rs {pricePerInsp.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm font-medium text-gray-800">
                  <span>{qty} x Rs {pricePerInsp.toLocaleString()}</span>
                  <span>Rs {subtotal.toLocaleString()}</span>
                </div>
              </div>
              <div className="px-4 py-3 border-t bg-green-50/60">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-green-800 whitespace-nowrap">Discount</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-gray-500">Rs</span>
                    <input type="text" inputMode="numeric" value={discountStr} placeholder="0"
                      onChange={e => setDiscountStr(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-28 h-9 border rounded-lg text-right text-sm font-semibold px-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none bg-white"
                      data-testid="pkg-discount-input" />
                  </div>
                </div>
              </div>
              <div className="px-4 py-4 border-t bg-white">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-900">Total Payable</span>
                  <span className="text-2xl font-black text-blue-700 tabular-nums" data-testid="pkg-final-amount">Rs {finalAmount.toLocaleString()}</span>
                </div>
                {qty > 0 && discountAmount > 0 && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed">
                    <span className="text-xs text-gray-500">Effective price per inspection</span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-emerald-700">Rs {effectivePerInsp.toLocaleString()}</span>
                      <span className="text-xs text-gray-400 line-through ml-2">Rs {pricePerInsp.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-1.5 flex-wrap">
              <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-[11px] font-semibold">{qty} Inspections</span>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md text-[11px] font-semibold">{validityDays} Days</span>
              {discountAmount > 0 && <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-md text-[11px] font-semibold">-Rs {discountAmount.toLocaleString()}</span>}
              {discountAmount > 0 && qty > 0 && <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-md text-[11px] font-semibold">Rs {effectivePerInsp.toLocaleString()}/insp</span>}
            </div>

            <div className="rounded-lg border bg-amber-50/50 p-3">
              <Label className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Payment Link Expires In</Label>
              <div className="flex gap-2 mt-2">
                {[{ label: '24 hrs', value: 24 }, { label: '48 hrs', value: 48 }, { label: '3 days', value: 72 }, { label: '7 days', value: 168 }].map(opt => (
                  <button key={opt.value} onClick={() => setLinkExpiryHours(opt.value)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${linkExpiryHours === opt.value ? 'bg-amber-600 text-white shadow-sm' : 'bg-white text-gray-600 border hover:border-amber-300 hover:text-amber-700'}`}
                    data-testid={`link-expiry-${opt.value}`}>{opt.label}</button>
                ))}
              </div>
              <p className="text-[10px] text-amber-700 mt-1.5">This price is valid only if paid within {linkExpiryHours} hours</p>
            </div>
          </>
        )}

        {!selectedTemplate && (
          <div className="text-center py-8 text-gray-400">
            <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Select a package to configure</p>
          </div>
        )}
      </div>

      {selectedTemplate && (
        <div className="px-6 py-4 border-t bg-gray-50 space-y-2.5 shrink-0">
          <Button onClick={handleSendPaymentLink} disabled={sendingLink || saving || finalAmount <= 0}
            className="w-full h-11 bg-green-600 hover:bg-green-700 text-sm font-semibold" data-testid="send-payment-link-btn">
            {sendingLink ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Phone className="h-4 w-4 mr-2" />}
            Send Payment Link via WhatsApp — Rs {finalAmount.toLocaleString()}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowPurchase(false); if (onCloseModal) onCloseModal(); }} className="flex-1 h-9 text-xs">Cancel</Button>
            <Button onClick={handleRazorpayCheckout} disabled={saving || sendingLink || finalAmount <= 0}
              className="flex-1 h-9 text-xs bg-blue-600 hover:bg-blue-700" data-testid="pay-now-btn">
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              <CreditCard className="h-3.5 w-3.5 mr-1" /> Pay Now (Razorpay)
            </Button>
            <Button variant="outline" onClick={handleRecordManualPayment} disabled={saving || sendingLink || finalAmount <= 0}
              className="flex-1 h-9 text-xs" data-testid="manual-payment-btn">
              Record Manual
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  );

  // When used as standalone modal (from table), only render the dialog
  if (autoOpenPurchase) {
    return (
      <Dialog open={showPurchase} onOpenChange={(open) => {
        setShowPurchase(open);
        if (!open && onCloseModal) onCloseModal();
      }}>
        {renderPurchaseDialogContent()}
      </Dialog>
    );
  }

  return (
    <div className="space-y-4" data-testid="dealer-packages-tab">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold text-gray-700">Purchased Packages</h4>
        <Button size="sm" onClick={openPurchaseModal} data-testid="purchase-package-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Package
        </Button>
      </div>

      {packages.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No packages purchased yet</p>}
      {packages.map((pkg) => {
        const pct = pkg.total_inspections > 0 ? Math.round((pkg.used_inspections / pkg.total_inspections) * 100) : 0;
        const isExpired = pkg.status === 'EXPIRED';
        const isUsed = pkg.status === 'FULLY_USED';
        return (
          <div key={pkg.id} className={`border rounded-lg p-3 ${isExpired ? 'bg-red-50 border-red-200' : isUsed ? 'bg-gray-50' : 'bg-white'}`} data-testid={`dealer-pkg-${pkg.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-sm font-semibold">{pkg.package_name}</p>
                <p className="text-xs text-gray-500">Purchased {new Date(pkg.purchased_at).toLocaleDateString('en-IN')} &middot; Rs {(pkg.amount_paid || 0).toLocaleString()}</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                pkg.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                pkg.status === 'EXPIRED' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`}>{pkg.status}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
              <span>{pkg.used_inspections}/{pkg.total_inspections} used</span>
              <span>{pkg.remaining_inspections} remaining</span>
              <span>Expires: {pkg.expires_at ? new Date(pkg.expires_at).toLocaleDateString('en-IN') : '-'}</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${pct >= 100 ? 'bg-gray-500' : pct >= 70 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}

      {/* ===== PURCHASE / PAYMENT LINK MODAL ===== */}
      <Dialog open={showPurchase} onOpenChange={(open) => {
        setShowPurchase(open);
        if (!open && onCloseModal) onCloseModal();
      }}>
        {renderPurchaseDialogContent()}
      </Dialog>
    </div>
  );
}

// ========== Dealer Detail Sheet ==========
function DealerDetailSheet({ dealer, open, onClose, employees, statuses, onUpdate, onDelete }) {
  const [activeTab, setActiveTab] = useState('info');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (dealer) setEditForm({ ...dealer });
  }, [dealer]);

  if (!dealer) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await dealersApi.update(dealer.id, editForm);
      toast.success('Dealer updated');
      setEditMode(false);
      if (onUpdate) onUpdate(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await dealersApi.update(dealer.id, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      if (onUpdate) onUpdate(res.data);
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete dealer "${dealer.business_name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await dealersApi.delete(dealer.id);
      toast.success(`Dealer "${dealer.business_name}" deleted`);
      if (onDelete) onDelete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await dealersApi.addActivity(dealer.id, { action: 'note_added', details: noteText });
      toast.success('Note added');
      setNoteText('');
      setActiveTab('activities');
    } catch { toast.error('Failed to add note'); }
  };

  const handleLogCall = async (callType) => {
    try {
      await dealersApi.addActivity(dealer.id, { action: callType, details: `Logged: ${callType}` });
      if (callType === 'rnr') {
        const res = await dealersApi.update(dealer.id, { status: 'RNR' });
        if (onUpdate) onUpdate(res.data);
      } else {
        if (onUpdate) onUpdate();
      }
      toast.success(`Logged: ${callType}`);
    } catch { toast.error('Failed to log'); }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="dealer-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Store className="h-5 w-5 text-blue-600" />
            {dealer.business_name}
          </SheetTitle>
        </SheetHeader>

        {/* Quick Status Bar */}
        <div className="flex flex-wrap gap-2 mt-3 mb-4">
          {statuses.map(s => (
            <button key={s.value} onClick={() => handleStatusChange(s.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                dealer.status === s.value ? DEALER_STATUS_COLORS[s.value] + ' ring-2 ring-offset-1 ring-current' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`} data-testid={`status-btn-${s.value}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-4">
          <Button size="sm" variant="outline" onClick={() => handleLogCall('called')} data-testid="log-call-btn"><Phone className="h-3.5 w-3.5 mr-1" />Called</Button>
          <Button size="sm" variant="outline" onClick={() => handleLogCall('rnr')} data-testid="log-rnr-btn">RNR</Button>
          <Button size="sm" variant="outline" onClick={() => handleLogCall('callback_requested')} data-testid="log-callback-btn"><Clock className="h-3.5 w-3.5 mr-1" />Callback</Button>
          <div className="ml-auto">
            <Button size="sm" variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="delete-dealer-btn">
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="info" className="flex-1">Info</TabsTrigger>
            <TabsTrigger value="activities" className="flex-1">Activities</TabsTrigger>
            <TabsTrigger value="packages" className="flex-1">Packages</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="mt-4 space-y-4">
            {editMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Dealer Name</Label><Input value={editForm.business_name || ''} onChange={e => setEditForm({ ...editForm, business_name: e.target.value })} /></div>
                  <div><Label className="text-xs">Contact Person</Label><Input value={editForm.contact_person || ''} onChange={e => setEditForm({ ...editForm, contact_person: e.target.value })} /></div>
                  <div><Label className="text-xs">Phone</Label><Input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                  <div><Label className="text-xs">Email</Label><Input value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })} /></div>
                  <div><Label className="text-xs">City</Label><Input value={editForm.city || ''} onChange={e => setEditForm({ ...editForm, city: e.target.value })} /></div>
                  <div><Label className="text-xs">State</Label><Input value={editForm.state || ''} onChange={e => setEditForm({ ...editForm, state: e.target.value })} /></div>
                  <div><Label className="text-xs">GST Number</Label><Input value={editForm.gst_number || ''} onChange={e => setEditForm({ ...editForm, gst_number: e.target.value })} /></div>
                  <div><Label className="text-xs">Dealer Type</Label>
                    <Select value={editForm.dealer_type || ''} onValueChange={v => setEditForm({ ...editForm, dealer_type: v })}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {['Multi Brand', 'Single Brand', 'Authorized Dealer', 'Independent', 'Franchise', 'Other'].map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs">Assigned To</Label>
                  <Select value={editForm.assigned_to || ''} onValueChange={v => {
                    const emp = employees.find(e => e.id === v);
                    setEditForm({ ...editForm, assigned_to: v, assigned_to_name: emp?.name || '' });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select executive" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div><span className="text-gray-500 text-xs">Dealer Name</span><p className="font-medium">{dealer.business_name}</p></div>
                  <div><span className="text-gray-500 text-xs">Phone</span><p className="font-medium">{dealer.phone}</p></div>
                  <div><span className="text-gray-500 text-xs">Email</span><p className="font-medium">{dealer.email || '-'}</p></div>
                  <div><span className="text-gray-500 text-xs">City</span><p className="font-medium">{dealer.city || '-'}</p></div>
                  <div><span className="text-gray-500 text-xs">State</span><p className="font-medium">{dealer.state || '-'}</p></div>
                  <div><span className="text-gray-500 text-xs">GST</span><p className="font-medium">{dealer.gst_number || '-'}</p></div>
                  <div><span className="text-gray-500 text-xs">Type</span><p className="font-medium">{dealer.dealer_type || '-'}</p></div>
                  <div><span className="text-gray-500 text-xs">Assigned To</span><p className="font-medium">{dealer.assigned_to_name || 'Unassigned'}</p></div>
                  <div><span className="text-gray-500 text-xs">Source</span><p className="font-medium">{dealer.source || '-'}</p></div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditMode(true)} data-testid="edit-dealer-btn"><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                {/* Add Note */}
                <div className="border-t pt-3 mt-3 space-y-2">
                  <Label className="text-xs font-semibold">Add Note</Label>
                  <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2} placeholder="Type a note..." />
                  <Button size="sm" onClick={handleAddNote} disabled={!noteText.trim()} data-testid="add-note-btn">Add Note</Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="activities" className="mt-4">
            <DealerActivityLog dealerId={dealer.id} />
          </TabsContent>

          <TabsContent value="packages" className="mt-4">
            <DealerPackagesTab dealerId={dealer.id} dealerName={dealer.business_name} dealerPhone={dealer.phone} onRefresh={() => { if (onUpdate) onUpdate(); }} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// ========== Inline Assign Popover ==========
function InlineAssign({ dealer, employees, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search
    ? employees.filter(e => e.name.toLowerCase().includes(search.toLowerCase()))
    : employees;

  const handleAssign = async (emp) => {
    setSaving(true);
    try {
      await dealersApi.update(dealer.id, { assigned_to: emp.id, assigned_to_name: emp.name });
      toast.success(`Assigned to ${emp.name}`);
      setOpen(false);
      onAssigned();
    } catch { toast.error('Failed to assign'); }
    finally { setSaving(false); }
  };

  const handleUnassign = async () => {
    setSaving(true);
    try {
      await dealersApi.update(dealer.id, { assigned_to: null, assigned_to_name: null });
      toast.success('Unassigned dealer');
      setOpen(false);
      onAssigned();
    } catch { toast.error('Failed to unassign'); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="text-left text-xs hover:bg-blue-50 px-2 py-1 rounded transition-colors w-full group" data-testid={`assign-trigger-${dealer.id}`}>
          {dealer.assigned_to_name
            ? <span className="text-gray-700 group-hover:text-blue-600">{dealer.assigned_to_name}</span>
            : <span className="text-gray-400 italic group-hover:text-blue-600">Unassigned</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-2 border-b">
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-xs" autoFocus data-testid="inline-assign-search" />
        </div>
        <ScrollArea className="max-h-48">
          <div className="p-1">
            {dealer.assigned_to_name && (
              <button onClick={handleUnassign} disabled={saving}
                className="w-full text-left px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors" data-testid="inline-unassign">
                Remove Assignment
              </button>
            )}
            {filtered.map(e => (
              <button key={e.id} onClick={() => handleAssign(e)} disabled={saving}
                className={`w-full text-left px-2 py-1.5 text-xs rounded transition-colors ${e.id === dealer.assigned_to ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                data-testid={`inline-assign-${e.id}`}>
                {e.name}
              </button>
            ))}
            {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No match</p>}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

// ========== Main Dealers Page ==========
export default function DealersPage() {
  const { user } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [cardCounts, setCardCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [cities, setCities] = useState([]);
  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [purchaseModalDealer, setPurchaseModalDealer] = useState(null); // For direct "+ New Package" from table
  // Date range
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Form state
  const [createForm, setCreateForm] = useState({ business_name: '', contact_person: '', phone: '', city: '', state: '', dealer_type: '', pincode: '' });
  const [saving, setSaving] = useState(false);
  const [selectedDealerIds, setSelectedDealerIds] = useState([]);
  const [pincodeLoading, setPincodeLoading] = useState(false);

  const fetchDealers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 50 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterCity) params.city = filterCity;
      if (filterAssigned) params.assigned_to = filterAssigned;
      if (dateFilter === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (dateFilter && dateFilter !== 'all') {
        params.date_filter = dateFilter;
      }
      const res = await dealersApi.getAll(params);
      setDealers(res.data.dealers);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setCities(res.data.cities || []);
    } catch { toast.error('Failed to load dealers'); }
    finally { setLoading(false); }
  }, [page, search, filterStatus, filterCity, filterAssigned, dateFilter, startDate, endDate]);

  const fetchCardCounts = useCallback(async () => {
    try {
      const params = {};
      if (filterAssigned) params.assigned_to = filterAssigned;
      if (dateFilter === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (dateFilter !== 'all') {
        params.date_filter = dateFilter;
      }
      const res = await dealersApi.getCardCounts(params);
      setCardCounts(res.data);
    } catch {}
  }, [filterAssigned, dateFilter, startDate, endDate]);

  useEffect(() => {
    dealersApi.getStatuses().then(r => setStatuses(r.data)).catch(() => {});
    dealersApi.getAssignableEmployees().then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchDealers(); }, [fetchDealers]);
  useEffect(() => { fetchCardCounts(); }, [fetchCardCounts]);

  const handleCreate = async () => {
    if (!createForm.business_name || !createForm.phone) { toast.error('Dealer name and phone required'); return; }
    setSaving(true);
    try {
      await dealersApi.create(createForm);
      toast.success('Dealer created');
      setCreateModal(false);
      setCreateForm({ business_name: '', contact_person: '', phone: '', city: '', state: '', dealer_type: '', pincode: '' });
      fetchDealers();
      fetchCardCounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const lookupPincode = async (pincode) => {
    if (!pincode || pincode.length < 5) return;
    setPincodeLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const keyRes = await fetch(`${API_URL}/api/settings/maps-key`);
      if (!keyRes.ok) { setPincodeLoading(false); return; }
      const { api_key } = await keyRes.json();
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${pincode}&region=in&key=${api_key}`);
      const data = await res.json();
      if (data.results?.length > 0) {
        const comps = data.results[0].address_components || [];
        let city = '', state = '';
        for (const c of comps) {
          if (c.types.includes('locality')) city = c.long_name;
          if (c.types.includes('administrative_area_level_3') && !city) city = c.long_name;
          if (c.types.includes('administrative_area_level_1')) state = c.long_name;
        }
        setCreateForm(prev => ({ ...prev, city: city || prev.city, state: state || prev.state }));
        if (city) toast.success(`Detected: ${city}, ${state}`);
      }
    } catch {
      // Silently fail
    } finally { setPincodeLoading(false); }
  };

  const toggleSelectDealer = (id) => {
    setSelectedDealerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="dealers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Store className="h-6 w-6 text-blue-600" /> Dealers (B2B)
          </h1>
          <p className="text-sm text-gray-500">{total} dealers</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setCreateModal(true)} data-testid="add-dealer-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Dealer
          </Button>
        </div>
      </div>

      {/* Date Filter + Stats */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5">
          {[{ label: 'All Time', value: 'all' }, { label: 'Today', value: 'today' }, { label: 'Yesterday', value: 'yesterday' }, { label: 'This Month', value: 'this_month' }, { label: 'Custom', value: 'custom' }].map(d => (
            <button key={d.value} onClick={() => { setDateFilter(d.value); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${dateFilter === d.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`date-filter-${d.value}`}>
              {d.label}
            </button>
          ))}
        </div>
        {dateFilter === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-lg" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-lg" />
          </div>
        )}
      </div>
      <DealerStats cardCounts={cardCounts} />

      {/* Search & Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search dealers..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-10" data-testid="dealer-search" />
        </div>
        <Select value={filterStatus} onValueChange={v => { setFilterStatus(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[190px]" data-testid="dealer-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="flex items-center justify-between gap-3 w-full">
                <span>All Statuses</span>
                <span className="text-xs text-slate-500 font-mono">{cardCounts?.total ?? ''}</span>
              </span>
            </SelectItem>
            {statuses.map(s => {
              const n = cardCounts?.status_breakdown?.[s.value] ?? 0;
              return (
                <SelectItem key={s.value} value={s.value} data-testid={`dealer-status-option-${s.value}`}>
                  <span className="flex items-center justify-between gap-3 w-full">
                    <span>{s.label}</span>
                    <span className={`text-xs font-mono ${n > 0 ? 'text-slate-600' : 'text-slate-300'}`}>
                      {n}
                    </span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Select value={filterCity} onValueChange={v => { setFilterCity(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Cities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAssigned} onValueChange={v => { setFilterAssigned(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[170px]"><SelectValue placeholder="All Executives" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Executives</SelectItem>
            {employees.map(e => (
              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left w-8">
                  <input type="checkbox" checked={selectedDealerIds.length === dealers.length && dealers.length > 0}
                    onChange={e => setSelectedDealerIds(e.target.checked ? dealers.map(d => d.id) : [])} className="rounded" />
                </th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">Created</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">Dealer Name</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">Phone</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">City</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">Assigned To</th>
                <th className="p-3 text-center text-xs font-medium text-gray-500 w-[130px]">Packages</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /></td></tr>}
              {!loading && dealers.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-gray-400">No dealers found</td></tr>}
              {!loading && dealers.map(d => (
                <tr key={d.id} className="border-b hover:bg-blue-50/30 cursor-pointer transition-colors" data-testid={`dealer-row-${d.id}`}
                  onClick={() => { setSelectedDealer(d); setDetailOpen(true); }}>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedDealerIds.includes(d.id)} onChange={() => toggleSelectDealer(d.id)} className="rounded" />
                  </td>
                  <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                  <td className="p-3">
                    <p className="font-medium text-gray-900">{d.business_name}</p>
                  </td>
                  <td className="p-3 text-gray-700">{d.phone}</td>
                  <td className="p-3 text-gray-600">{d.city || '-'}</td>
                  <td className="p-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${DEALER_STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <InlineAssign dealer={d} employees={employees} onAssigned={() => { fetchDealers(); fetchCardCounts(); }} />
                  </td>
                  <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                    {d.pkg_remaining > 0 ? (
                      <span className="text-xs font-semibold text-emerald-700">{d.pkg_used}/{d.pkg_total} used &middot; {d.pkg_remaining} left</span>
                    ) : (
                      <button onClick={() => setPurchaseModalDealer(d)}
                        className="px-2.5 py-1.5 text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors whitespace-nowrap"
                        data-testid={`new-pkg-${d.id}`}>
                        + New Package
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-xs text-gray-500">Page {page} of {totalPages} ({total} total)</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Dealer Detail Sheet */}
      <DealerDetailSheet dealer={selectedDealer} open={detailOpen} onClose={() => { setDetailOpen(false); setSelectedDealer(null); }}
        employees={employees} statuses={statuses}
        onUpdate={(updatedDealer) => {
          if (updatedDealer) setSelectedDealer(updatedDealer);
          fetchDealers(); fetchCardCounts();
        }}
        onDelete={() => { setDetailOpen(false); setSelectedDealer(null); fetchDealers(); fetchCardCounts(); }}
      />

      {/* Create Dealer Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add New Dealer</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Dealer Name *</Label><Input value={createForm.business_name} onChange={e => setCreateForm({ ...createForm, business_name: e.target.value })} data-testid="create-dealer-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact Person</Label><Input value={createForm.contact_person} onChange={e => setCreateForm({ ...createForm, contact_person: e.target.value })} /></div>
              <div><Label>Phone *</Label><Input value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} data-testid="create-dealer-phone" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Pincode</Label>
                <div className="relative">
                  <Input value={createForm.pincode} placeholder="560001"
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setCreateForm({ ...createForm, pincode: val });
                      if (val.length === 6) lookupPincode(val);
                    }}
                    data-testid="create-dealer-pincode" />
                  {pincodeLoading && <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2.5 top-2.5 text-gray-400" />}
                </div>
              </div>
              <div><Label>City</Label><Input value={createForm.city} onChange={e => setCreateForm({ ...createForm, city: e.target.value })} data-testid="create-dealer-city" /></div>
              <div><Label>State</Label><Input value={createForm.state} onChange={e => setCreateForm({ ...createForm, state: e.target.value })} /></div>
            </div>
            <div>
              <Label>Dealer Type</Label>
              <Select value={createForm.dealer_type} onValueChange={v => setCreateForm({ ...createForm, dealer_type: v })}>
                <SelectTrigger data-testid="create-dealer-type"><SelectValue placeholder="Select dealer type" /></SelectTrigger>
                <SelectContent>
                  {['Multi Brand', 'Single Brand', 'Authorized Dealer', 'Independent', 'Franchise', 'Other'].map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateModal(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="save-dealer-btn">
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Create Dealer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Standalone Package Purchase Modal — triggered from table "+ New Package" */}
      {purchaseModalDealer && (
        <DealerPackagesTab
          dealerId={purchaseModalDealer.id}
          dealerName={purchaseModalDealer.business_name}
          dealerPhone={purchaseModalDealer.phone}
          onRefresh={() => { fetchDealers(); fetchCardCounts(); setPurchaseModalDealer(null); }}
          autoOpenPurchase={true}
          onCloseModal={() => setPurchaseModalDealer(null)}
        />
      )}
    </div>
  );
}
